'use client';

import { useSearchParams } from 'next/navigation';
import React, {
  FC,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DynamoTabBlok } from '../dynamo-tab/dynamo-tab.blok';
import { TabsContext } from './tabs.context';

export interface TabsProviderProps {
  defaultActiveTabId: string;
  children: ReactNode;
  persist: boolean;
  items: Array<DynamoTabBlok>;
}

export const TABS_PERSIST_KEY = '_t';

export const TabsProvider: FC<TabsProviderProps> = ({
  defaultActiveTabId,
  persist,
  items,
  children,
}) => {
  const searchParamsHook = useSearchParams();
  const clientSideTabId = searchParamsHook?.get(TABS_PERSIST_KEY);

  const initialActiveTab = clientSideTabId || defaultActiveTabId;
  const [activeTabId, setActiveTabId] = useState(initialActiveTab);
  const [tabItems] = useState(items || []);
  const tabRefs = useRef<Array<HTMLElement>>([]);

  // Use ref to track if we're updating from URL to prevent loops
  const isUpdatingFromUrl = useRef(false);

  // Listen for URL changes (browser back/forward navigation)
  useEffect(() => {
    if (!persist) return;

    const urlTabId = searchParamsHook?.get(TABS_PERSIST_KEY);

    // Mark that we're updating from URL
    isUpdatingFromUrl.current = true;

    if (urlTabId) {
      setActiveTabId(urlTabId);
    } else {
      setActiveTabId(defaultActiveTabId);
    }

    // Reset the flag after a microtask
    Promise.resolve().then(() => {
      isUpdatingFromUrl.current = false;
    });
  }, [searchParamsHook, persist, defaultActiveTabId]);

  // Update URL when activeTabId changes programmatically
  useEffect(() => {
    // Skip if we're updating from URL to prevent loops
    if (isUpdatingFromUrl.current || typeof window === 'undefined') return;

    const currentQueryParam = searchParamsHook?.get(TABS_PERSIST_KEY);

    if (persist) {
      if (currentQueryParam !== activeTabId) {
        const params = new URLSearchParams(searchParamsHook?.toString() || '');
        params.set(TABS_PERSIST_KEY, activeTabId);
        const newUrl = params.toString()
          ? `?${params.toString()}`
          : window.location.pathname;
        window.history.replaceState(null, '', newUrl);
      }
    } else {
      if (currentQueryParam !== null) {
        const params = new URLSearchParams(searchParamsHook?.toString() || '');
        params.delete(TABS_PERSIST_KEY);
        const newSearch = params.toString();
        window.history.replaceState(
          null,
          '',
          newSearch ? `?${newSearch}` : window.location.pathname
        );
      }
    }
  }, [activeTabId, persist, searchParamsHook]);

  const contextValue = useMemo(
    () => ({
      activeTabId,
      setActiveTabId,
      persist,
      tabItems,
      tabRefs,
    }),
    [activeTabId, persist, tabItems, tabRefs]
  );

  return (
    <TabsContext.Provider value={contextValue}>{children}</TabsContext.Provider>
  );
};